import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import LoadingIndicator from 'src/components/LoadingIndicator';
import { getBrandingColor, colorTypes } from 'src/branding';
import { getImpersonateParamIfSuperuser } from 'src/utils/routes.utils';
import { Breadcrumbs } from 'src/components/Breadcrumbs';
import { Column, Row, Label } from '../../components/Layout';
import { SearchableDropdown } from '../../components/SearchableDropdown';
import { format } from '../../utils/date.utils';
import { backendUrl } from '../../config';
import { DateDropdownItem } from '../Accountings/Components';
import { formatAmount } from '../Accountings/util';
import Button from '../../components/Button';
import Helptext from '../../components/Helptext';
import { getDocuments } from '../../store/accountings/accountings.selectors';
import { accountingsActions } from '../../store/accountings/accountings.actions';
import axiosInstance from 'src/axios-instance';
import { DocumentCategory, DocumentPage, SeachableDropdownWrapper } from './Components';

const Documents = () => {
  const dispatch = useDispatch();
  const documents = useSelector(getDocuments);
  const data = documents.data || {};
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedCreditNote, setSelectedCreditNote] = useState(null);
  const [selectedAccounting, setSelectedAccounting] = useState(null);
  const [selectedOpenPost, setSelectedOpenPost] = useState(null);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);

  useEffect(() => {
    if (documents.data) {
      axiosInstance.request({
        method: 'POST',
        url: `${backendUrl}/api/markread/documents/notification`,
        withCredentials: true,
      });
      const {
        invoices,
        credit_notes,
        accountings,
        open_posts,
        withdrawals,
      } = documents.data;
      if (invoices[0]) {
        setSelectedInvoice(invoices[0]);
      }
      if (credit_notes[0]) {
        setSelectedCreditNote(credit_notes[0]);
      }
      if (accountings[0]) {
        setSelectedAccounting(accountings[0]);
      }
      if (open_posts[0]) {
        setSelectedOpenPost(open_posts[0]);
      }
      if (withdrawals[0]) {
        setSelectedWithdrawal(withdrawals[0]);
      }
    } else if (!documents.isFetching && !documents.error) {
      dispatch(accountingsActions.fetchDocuments());
    }
  }, [dispatch, documents.data, documents.error, documents.isFetching]);

  return (
    <DocumentPage
      title="Dokumenter"
      style={{
        maxWidth: '750px',
        alignSelf: 'center',
        width: '80%',
        paddingLeft: '1em',
        paddingRight: '2em',
        minHeight: '1200px',
      }}
    >
      <Breadcrumbs
        breadcrumbs={[{ title: 'Dokumenter', url: '/documents' }]}
        wrapperStyle={css`
            margin-bottom: 0.6rem; `}
      />
      <Label as="h1" marginTop="0.5em" marginBottom="0.5em">
        Dokumenter
      </Label>
      {documents.isFetching && <LoadingIndicator withText />}
      {documents.error && <h2>Kunne ikke laste inn dokumenter.</h2>}
      {data.invoices && !!data.invoices.length && (
        <DocumentCategory>
          <div style={{ minHeight: 'fit-content' }}>
            <Label as="h2" marginTop="0.5em" marginBottom="0.5em">
              Bokførte faktura
            </Label>
            {data.invoices.length > 1 && (
              <Helptext
                render={(text) => (text && (
                  <Label color="#686a6e" marginBottom="2em">
                    {text}
                  </Label>
                )) || <Label marginBottom="1em" />}
                name="search-documents"
              />
            )}
          </div>
          {data.invoices.length > 1 && (
            <SeachableDropdownWrapper>
              <SearchableDropdown
                placeholder="Søk etter dokument"
                itemSearchKey="formatted"
                smallScreenwidth="95%"
                bigScreenWidth="430px"
                bigScreenWidthResultList="430"
                smallScreenWidthResultList="95%"
                selectedItem={selectedInvoice}
                renderSelectedItemInInput={(selected) => selected && selected.formatted}
                renderItem={(item, searchTerm, itemHasFocus) => (
                  <DateDropdownItem
                    smallFontOnSmallScreens
                    key={searchTerm}
                    searchTerm={searchTerm}
                    itemHasFocus={itemHasFocus}
                    item={item}
                    fontSize="0.9em"
                  />
                )}
                dataitems={data.invoices || []}
                onSelectedChange={(item) => item && setSelectedInvoice(item)}
              />
            </SeachableDropdownWrapper>
          )}
          {selectedInvoice && (
            <DocumentBox shrink marginTop="1em">
              <DocumentInner shrink>
                <Column
                  shrink
                  grow
                  style={{ flexDirection: 'row' }}
                  marginRight="1em"
                >
                  <div>
                    <Label>
                      Fakturanummer:
                      {' '}
                      {selectedInvoice.document_no}
                    </Label>
                    <Label>
                      Fakturadato:
                      {' '}
                      {format(new Date(selectedInvoice.date), 'dd. MMMM yyyy')}
                    </Label>
                    {selectedInvoice.amount && (
                      <Label>
                        Sum: kr
                        {' '}
                        {formatAmount(selectedInvoice.amount)}
                      </Label>
                    )}
                  </div>
                </Column>
                {selectedInvoice.has_file && (
                  <Column marginLeft="auto" name="showPDF" marginTop="1em">
                    <Button
                      primary
                      download
                      target="_blank"
                      href={`${backendUrl}/api/documents/download/${selectedInvoice.download_id}${getImpersonateParamIfSuperuser()}`}
                    >
                      Last ned PDF
                    </Button>
                  </Column>
                )}
              </DocumentInner>
            </DocumentBox>
          )}
        </DocumentCategory>
      )}
      {data.credit_notes && !!data.credit_notes.length && (
        <DocumentCategory>
          <div style={{ minHeight: 'fit-content' }}>
            <Label as="h2" marginTop="2em" marginBottom="0.5em">
              Bokførte kreditnota
            </Label>
            {data.credit_notes.length > 1 && (
              <Helptext
                render={(text) => (text && (
                  <Label color="#686a6e" marginBottom="2em">
                    {text}
                  </Label>
                )) || <Label marginBottom="1em" />}
                name="search-documents-credit-note"
              />
            )}
          </div>
          {data.credit_notes.length > 1 && (
            <SeachableDropdownWrapper>
              <SearchableDropdown
                placeholder="Søk etter dokument"
                itemSearchKey="formatted"
                smallScreenwidth="95%"
                bigScreenWidth="430px"
                bigScreenWidthResultList="430"
                smallScreenWidthResultList="95%"
                selectedItem={selectedCreditNote}
                renderSelectedItemInInput={(selected) => selected && selected.formatted}
                renderItem={(item, searchTerm, itemHasFocus) => (
                  <DateDropdownItem
                    smallFontOnSmallScreens
                    key={searchTerm}
                    searchTerm={searchTerm}
                    itemHasFocus={itemHasFocus}
                    item={item}
                    fontSize="0.9em"
                  />
                )}
                dataitems={data.credit_notes || []}
                onSelectedChange={(item) => item && setSelectedCreditNote(item)}
              />
            </SeachableDropdownWrapper>
          )}
          {selectedCreditNote && (
            <DocumentBox shrink marginTop="1em">
              <DocumentInner shrink>
                <Column
                  shrink
                  grow
                  style={{ flexDirection: 'row' }}
                  marginRight="1em"
                >
                  <div>
                    <Label>
                      Dokumentnummer:
                      {' '}
                      {selectedCreditNote.document_no}
                    </Label>
                    <Label>
                      Dato:
                      {' '}
                      {format(
                        new Date(selectedCreditNote.date),
                        'dd. MMMM yyyy',
                      )}
                    </Label>
                    {selectedCreditNote.amount && (
                      <Label>
                        Sum: kr
                        {' '}
                        {formatAmount(selectedCreditNote.amount)}
                      </Label>
                    )}
                  </div>
                </Column>
                {selectedCreditNote.has_file && (
                  <Column marginLeft="auto" name="showPDF" marginTop="1em">
                    <Button
                      primary
                      target="_blank"
                      download
                      href={`${backendUrl}/api/documents/download/${selectedCreditNote.download_id}${getImpersonateParamIfSuperuser()}`}
                    >
                      Last ned PDF
                    </Button>
                  </Column>
                )}
              </DocumentInner>
            </DocumentBox>
          )}
        </DocumentCategory>
      )}
      {data.accountings && !!data.accountings.length && (
        <DocumentCategory>
          <div style={{ minHeight: 'fit-content' }}>
            <Label as="h2" marginTop="2em" marginBottom="0.5em">
              Bokførte avregninger
            </Label>
            {data.accountings.length > 1 && (
              <Helptext
                render={(text) => (text && (
                  <Label color="#686a6e" marginBottom="2em">
                    {text}
                  </Label>
                )) || <Label marginBottom="1em" />}
                name="search-documents-accountings"
              />
            )}
          </div>
          {data.accountings.length > 1 && (
            <SeachableDropdownWrapper>
              <SearchableDropdown
                placeholder="Søk etter dokument"
                itemSearchKey="formatted"
                smallScreenwidth="95%"
                bigScreenWidth="430px"
                bigScreenWidthResultList="430"
                smallScreenWidthResultList="95%"
                selectedItem={selectedAccounting}
                renderSelectedItemInInput={(selected) => selected && selected.formatted}
                renderItem={(item, searchTerm, itemHasFocus) => (
                  <DateDropdownItem
                    smallFontOnSmallScreens
                    key={searchTerm}
                    searchTerm={searchTerm}
                    itemHasFocus={itemHasFocus}
                    item={item}
                    fontSize="0.9em"
                  />
                )}
                dataitems={data.accountings || []}
                onSelectedChange={(item) => item && setSelectedAccounting(item)}
              />
            </SeachableDropdownWrapper>
          )}
          {selectedAccounting && (
            <DocumentBox shrink marginTop="1em">
              <DocumentInner shrink>
                <Column
                  shrink
                  grow
                  style={{ flexDirection: 'row' }}
                  marginRight="1em"
                >
                  <div>
                    <Label>
                      Dokumentnummer:
                      {' '}
                      {selectedAccounting.document_no}
                    </Label>
                    <Label>
                      Dato:
                      {' '}
                      {format(
                        new Date(selectedAccounting.date),
                        'dd. MMMM yyyy',
                      )}
                    </Label>
                    {selectedAccounting.amount && (
                      <Label>
                        Sum: kr
                        {' '}
                        {formatAmount(selectedAccounting.amount)}
                      </Label>
                    )}
                  </div>
                </Column>
                {selectedAccounting.has_file && (
                  <Column marginLeft="auto" name="showPDF" marginTop="1em">
                    <Button
                      primary
                      download
                      target="_blank"
                      href={`${backendUrl}/api/documents/download/${selectedAccounting.download_id}${getImpersonateParamIfSuperuser()}`}
                    >
                      Last ned PDF
                    </Button>
                  </Column>
                )}
              </DocumentInner>
            </DocumentBox>
          )}
        </DocumentCategory>
      )}
      {data.open_posts && !!data.open_posts.length && (
        <DocumentCategory>
          <div style={{ minHeight: 'fit-content' }}>
            <Label as="h2" marginTop="1.5em" marginBottom="0.5em">
              Åpne poster / årssammendrag:
            </Label>
            {data.open_posts.length > 1 && (
              <Helptext
                render={(text) => (text && (
                  <Label color="#686a6e" marginBottom="2em">
                    {text}
                  </Label>
                )) || <Label marginBottom="1em" />}
                name="search-documents-open-posts"
              />
            )}
          </div>
          {data.open_posts.length > 1 && (
            <SeachableDropdownWrapper>
              <SearchableDropdown
                placeholder="Søk etter dokument"
                itemSearchKey="formatted"
                smallScreenwidth="95%"
                bigScreenWidth="430px"
                bigScreenWidthResultList="430"
                smallScreenWidthResultList="95%"
                selectedItem={selectedOpenPost}
                renderSelectedItemInInput={(selected) => selected && selected.formatted}
                renderItem={(item, searchTerm, itemHasFocus) => (
                  <DateDropdownItem
                    smallFontOnSmallScreens
                    key={searchTerm}
                    searchTerm={searchTerm}
                    itemHasFocus={itemHasFocus}
                    item={item}
                    fontSize="0.9em"
                  />
                )}
                dataitems={data.open_posts || []}
                onSelectedChange={(item) => item && setSelectedOpenPost(item)}
              />
            </SeachableDropdownWrapper>
          )}
          {selectedOpenPost && (
            <DocumentBox shrink marginTop="1em">
              <DocumentInner shrink>
                <Column shrink grow>
                  <Label>
                    Dato:
                    {' '}
                    {format(new Date(selectedOpenPost.date), 'dd. MMMM yyyy')}
                  </Label>
                </Column>
                <Column marginTop="1em">
                  <Button
                    primary
                    download
                    target="_blank"
                    href={`${backendUrl}/api/documents/download/${selectedOpenPost.download_id}${getImpersonateParamIfSuperuser()}`}
                  >
                    Last ned PDF
                  </Button>
                </Column>
              </DocumentInner>
            </DocumentBox>
          )}
        </DocumentCategory>
      )}
      {data.withdrawals && !!data.withdrawals.length && (
        <DocumentCategory>
          <div style={{ minHeight: 'fit-content' }}>
            <Label as="h2" marginTop="1.5em" marginBottom="0.5em">
              Kontoutdrag / årssammendrag:
            </Label>
            {data.withdrawals.length > 1 && (
              <Helptext
                render={(text) => (text && (
                  <Label color="#686a6e" marginBottom="2em">
                    {text}
                  </Label>
                )) || <Label marginBottom="1em" />}
                name="search-documents-withdrawal"
              />
            )}
          </div>
          {data.withdrawals.length > 1 && (
            <SeachableDropdownWrapper>
              <SearchableDropdown
                placeholder="Søk etter dokument"
                itemSearchKey="formatted"
                smallScreenwidth="95%"
                bigScreenWidth="430px"
                bigScreenWidthResultList="430"
                smallScreenWidthResultList="95%"
                selectedItem={selectedWithdrawal}
                renderSelectedItemInInput={(selected) => selected && selected.formatted}
                renderItem={(item, searchTerm, itemHasFocus) => (
                  <DateDropdownItem
                    smallFontOnSmallScreens
                    key={searchTerm}
                    searchTerm={searchTerm}
                    itemHasFocus={itemHasFocus}
                    item={item}
                    fontSize="0.9em"
                  />
                )}
                dataitems={data.withdrawals || []}
                onSelectedChange={(item) => item && setSelectedWithdrawal(item)}
              />
            </SeachableDropdownWrapper>
          )}
          {selectedWithdrawal && (
            <DocumentBox shrink marginTop="1em">
              <DocumentInner shrink>
                <Column shrink grow>
                  <Label>
                    Dato:
                    {' '}
                    {format(new Date(selectedWithdrawal.date), 'dd. MMMM yyyy')}
                  </Label>
                </Column>
                <Column marginTop="1em">
                  <Button
                    primary
                    download
                    target="_blank"
                    href={`${backendUrl}/api/documents/download/${selectedWithdrawal.download_id}${getImpersonateParamIfSuperuser()}`}
                  >
                    Last ned PDF
                  </Button>
                </Column>
              </DocumentInner>
            </DocumentBox>
          )}
        </DocumentCategory>
      )}
    </DocumentPage>
  );
};

const DocumentBox = styled(Column)`
  padding: 1.5em;
  border-radius: 5px;
  background-color: ${getBrandingColor(colorTypes.whiteAltBg)};
  min-height: fit-content;
  @media screen and (max-width: 460px){
    width: 100%;
  }

`;
const DocumentInner = styled(Row)`
  border-top: 1px solid #e6e3df;
  padding-top: 1em;
  margin-top: 1em;

  padding-top: 0;
  :first-child {
    border-top: none;
    margin-top: 0;
  }

  @media screen and (max-width: 400px) {
    flex-direction: column;

    [name='showPDF'] {
      align-self: center;
      margin-left: 0;
      margin-top: 0.7em;
    }
  }
`;

export default Documents;
